// What happens to a picture the owner pastes (or drops) into the composer body.
//
//   size check -> compress -> upload to Cloud Storage -> insert <img src="<download url>">
//
// If the upload fails (offline, signed out, quota) the picture is NOT lost: it is embedded as a data: URL, the way
// it always was, so a paste can never be refused just because Storage is unavailable. (An embedded picture still
// counts toward the draft's 1 MiB cap; the composer offloads those to Storage on the next save - see
// TerminalRichText.offloadInlineImages.)
//
// The editor supplies the real browser pieces; everything is injected here so each branch is unit-tested.

import { MAIL_ATTACH_MAX_FILE_BYTES, formatBytes, formatLimit } from './attachmentLimits';

export interface IngestDeps {
  /** Shrink a picture. Must not reject (compressImageForEmail falls back to the original). */
  compress: (file: Blob, name: string) => Promise<{ blob: Blob; name: string }>;
  /** Upload and return the URL for the body. Absent = embed as a data: URL (the legacy behaviour). */
  upload?: (blob: Blob, name: string) => Promise<{ src: string; bytes?: number }>;
  /** Per picture: false = skip the upload and embed it (a type the server will not embed as a stored picture). Default: upload. */
  shouldUpload?: (blob: Blob) => boolean;
  toDataUrl: (blob: Blob) => Promise<string>;
  insert: (src: string) => void;
  /** Checked just before each insert: the host may have locked the editor (e.g. a send started) meanwhile. */
  isEditable: () => boolean;
  /** +1 when an upload starts, -1 when it ends, so the composer can hold Send while one is running. */
  onPending?: (delta: 1 | -1) => void;
  /** Why a picture was not inserted - shown to the owner. */
  onReject?: (reason: string) => void;
  warn?: (message: string, err?: unknown) => void;
}

export interface IngestResult {
  inserted: number;
  uploaded: number;
  embedded: number;
  rejected: number;
}

const EXT_BY_TYPE: Record<string, string> = {
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp', 'image/bmp': 'bmp',
};

/** The browser's generic clipboard name ("image.png") says nothing and collides: give it a unique, readable one. */
export function nameForPastedImage(file: Blob, index: number, now: number = Date.now()): string {
  const raw = typeof (file as File).name === 'string' ? (file as File).name : '';
  if (raw && raw !== 'image.png') return raw;
  const ext = EXT_BY_TYPE[String(file.type || '').toLowerCase()] || 'png';
  return `pasted-image-${now}-${index + 1}.${ext}`;
}

/** Only these are pasted as pictures; anything else falls back to the browser's normal paste. */
export function isPastableImage(type: string): boolean {
  return /^image\/(png|jpe?g|gif|webp|bmp)$/i.test(String(type || ''));
}

/** The image Files in a clipboard / drop file list, in order. */
export function imageFilesOf(list: ArrayLike<{ type?: string }> | null | undefined): File[] {
  const out: File[] = [];
  if (!list) return out;
  for (let i = 0; i < list.length; i++) {
    const f = list[i] as File;
    if (f && isPastableImage(f.type)) out.push(f);
  }
  return out;
}

/**
 * Process the pictures one after another (so they land in the order pasted). Never throws: a picture that cannot
 * be handled is reported through onReject and skipped, and the rest carry on.
 */
export async function ingestImageFiles(files: Blob[], deps: IngestDeps): Promise<IngestResult> {
  const result: IngestResult = { inserted: 0, uploaded: 0, embedded: 0, rejected: 0 };
  const reject = (reason: string) => { result.rejected += 1; deps.onReject?.(reason); };

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const name = nameForPastedImage(file, i);
    if (!(file.size > 0)) { reject(`${name} is empty (0 B) - nothing to paste`); continue; }
    if (file.size > MAIL_ATTACH_MAX_FILE_BYTES) {
      reject(`${name} is ${formatBytes(file.size)} - over the ${formatLimit(MAIL_ATTACH_MAX_FILE_BYTES)} per-file limit`);
      continue;
    }

    let small: { blob: Blob; name: string } = { blob: file, name };
    try {
      small = await deps.compress(file, name);
    } catch (err) {
      deps.warn?.('image compression failed - using the original', err);
    }

    let src: string | null = null;
    let uploaded = false;
    if (deps.upload && (!deps.shouldUpload || deps.shouldUpload(small.blob))) {
      deps.onPending?.(1);
      try {
        src = (await deps.upload(small.blob, small.name)).src;
        uploaded = true;
      } catch (err) {
        deps.warn?.('image upload failed - embedding the picture in the draft instead', err);
      } finally {
        deps.onPending?.(-1);
      }
    }
    if (src === null) {
      try {
        src = await deps.toDataUrl(small.blob);
      } catch (err) {
        deps.warn?.('could not read the pasted image', err);
        reject(`could not read ${name}`);
        continue;
      }
    }

    if (!deps.isEditable()) break;
    deps.insert(src);
    result.inserted += 1;
    if (uploaded) result.uploaded += 1; else result.embedded += 1;
  }
  return result;
}
